import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { HotToastService } from '@ngxpert/hot-toast';
import { Observable } from 'rxjs';

import { ReachBookmark } from '@reach/interfaces/bookmarks/bookmarks.interface';
import { BrowserBookmarksBrowser } from '@reach/interfaces/bookmarks/bookmarks-browser.enum';
import { ErrorCodes } from '@reach/interfaces/error/error-codes.enum';
import {
	AnyImportCsvNode,
	ImportCsvInterface,
} from '@reach/interfaces/integrations/csv/csv.interface';
import {
	AuthorizeGoogleDriveIntegrationResponse,
	ExecuteGoogleDriveIntegrationResponse,
	GetOrRefreshAccessTokenGoogleDriveIntegrationResponse,
	GetSyncedIdsGoogleDriveIntegrationResponse,
	RegisterGoogleDriveIntegrationResponse,
	SyncGoogleDriveIntegrationResponse,
	UpdateSyncedStateGoogleDriveIntegrationResponse,
} from '@reach/interfaces/integrations/google-drive/google-drive-integration.responses';
import { IntegrationExecutionInterface } from '@reach/interfaces/integrations/integration-execution.interface';
import { ReachIntegration } from '@reach/interfaces/integrations/integration-type.enum';
import { IntegrationSetExecutionNotificationsRequest } from '@reach/interfaces/integrations/integrations.requests';
import {
	DeleteIntegrationResponse,
	GetIntegrationExecutionResponse,
	GetIntegrationExecutionsResponse,
	GetIntegrationResponse,
	IntegrationSetExecutionNotificationsResponse,
	ListIntegrationsByUserResponse,
	SetIntegrationSyncStatusResponse,
} from '@reach/interfaces/integrations/integrations.responses';
import { PhoneContactsRequestType } from '@reach/interfaces/integrations/phone-contacts/phone-contacts-integration.interface';
import {
	GetSharepointIntegrationColumnSelectionsRequest,
	ReauthenticateSharepointIntegrationRequest,
	RegisterSharepointIntegrationRequest,
	SharepointColumnSelectionInterface,
	UpdateSharepointIntegrationColumnSelectionsRequest,
} from '@reach/interfaces/integrations/sharepoint/sharepoint-integration.requests';
import {
	ExecuteSharepointIntegrationResponse,
	GenerateSharepointAuthUrlResponse,
	GetSharepointIntegrationColumnSelectionsResponse,
	ReauthenticateSharepointIntegrationResponse,
	RedeemSharepointAuthCodeResponse,
	RegisterSharepointIntegrationResponse,
	SyncSharepointIntegrationResponse,
	UpdateSharepointIntegrationColumnSelectionsResponse,
} from '@reach/interfaces/integrations/sharepoint/sharepoint-integration.responses';
import { SharepointEntryInterface } from '@reach/interfaces/integrations/sharepoint/sharepoint-integration-entry.interface';
import { SitemapSyncResponse } from '@reach/interfaces/integrations/sitemap/sitemap-integration.responses';
import { GetJobsResponse } from '@reach/interfaces/jobs/jobs.responses';
import { NodeCreationSource } from '@reach/interfaces/nodes/node-creation-source.interface';
import { OrderByEnum } from '@reach/interfaces/requests/request-pagination.interface';

import { BaseApiService } from '~app-client/api/_internal';
import { ReachBlobDownloaderService } from '~app-client/core/services';
import { CLIENT_ENVIRONMENT } from '~app-client/core/tokens';
import { ReachNodeId } from '~app-client/core/types';
import {
	ReachImport,
	ReachIntegrationOrImport,
} from '~app-client-item/integrations/types/integrations-imports.type';

import { ApiParams } from '../_internal/http.service';
import { ClientApiError } from '../types/error';

export const INTEGRATION_EXECUTIONS_PAGE_SIZE = 500;

@Injectable({ providedIn: 'root' })
export class IntegrationsApiService extends BaseApiService {
	protected commonPath = ['integrations'];
	protected commonIntegrationPath = ['integrations'];
	protected integrationsUrl = this.urlFnGenerator(this.commonIntegrationPath);
	protected commonNotesPath = ['notes'];
	protected notesUrl = this.urlFnGenerator(this.commonNotesPath);

	private readonly blobDownloaderService = inject(ReachBlobDownloaderService);
	private readonly hotToastService = inject(HotToastService);
	private readonly transloco = inject(TranslocoService);

	protected readonly environment = inject(CLIENT_ENVIRONMENT);

	public async getList(): Promise<ListIntegrationsByUserResponse> {
		return await this.http.get<ListIntegrationsByUserResponse>(this.integrationsUrl(), {
			t: this.paramTime,
		});
	}

	public async getIntegration(
		integrationId: string,
		privateId?: string
	): Promise<GetIntegrationResponse> {
		const params: ApiParams = { t: this.paramTime };
		if (privateId) {
			params.privateId = privateId;
		}
		return await this.http.get<GetIntegrationResponse>(
			this.integrationsUrl(integrationId),
			params
		);
	}

	public getIntegrationAsObs(integrationId: string): Observable<GetIntegrationResponse> {
		return this.http.getObs<GetIntegrationResponse>(this.integrationsUrl(integrationId), {
			t: this.paramTime,
		});
	}

	public async createCsvIntegration(
		workspaceName: string,
		items: AnyImportCsvNode[],
		isFirstBatch: boolean,
		relationshipNames?: string[]
	): Promise<IntegrationExecutionInterface> {
		const body: ImportCsvInterface & { isFirstBatch: boolean } = {
			items,
			fileName: workspaceName,
			isFirstBatch,
			relationshipNames,
		};
		return await this.http
			.post<IntegrationExecutionInterface>(this.integrationsUrl('csv'), body)
			.catch(this.handleSameExecutionRunningError(ReachIntegration.CSV));
	}

	public async downloadCsvTemplate(fileName: string): Promise<Blob> {
		const headers = { ['Content-Type']: 'text/csv; charset=utf-8' };
		const blob = await this.http.post<Blob>(
			this.integrationsUrl('csv', 'template'),
			{},
			{},
			headers,
			'blob'
		);

		await this.blobDownloaderService.downloadBlob(blob, `${fileName}.csv`);
		return blob;
	}

	public async getIntegrationExecutions(
		integrationId: string,
		page: number
	): Promise<GetIntegrationExecutionsResponse> {
		return await this.http.get<GetIntegrationExecutionsResponse>(
			this.integrationsUrl(integrationId, 'executions'),
			{
				t: this.paramTime,
				page: String(page),
				size: String(INTEGRATION_EXECUTIONS_PAGE_SIZE),
				sortBy: 'createdAt',
				orderBy: OrderByEnum.DESC,
			}
		);
	}

	public async deleteIntegration(integrationId: string): Promise<DeleteIntegrationResponse> {
		return await this.http.delete<DeleteIntegrationResponse>(
			this.integrationsUrl(integrationId)
		);
	}

	public async executePhoneContacts(
		body: PhoneContactsRequestType
	): Promise<GetIntegrationExecutionResponse> {
		return await this.http
			.post<GetIntegrationExecutionResponse>(this.integrationsUrl('contacts'), body)
			.catch(this.handleSameExecutionRunningError(ReachIntegration.PHONE_CONTACTS));
	}

	public async executeGoogleDocs(
		files: File[],
		isFirstBatch: boolean,
		workspaceIds: ReachNodeId[],
		source: NodeCreationSource
	): Promise<GetIntegrationExecutionResponse> {
		const body = new FormData();
		for (const file of files) {
			// Check if the file has a webkitRelativePath property and use it as the filename. We have to do it like this for the Safari browser.
			const filename = file.webkitRelativePath || file.name;
			body.append('files', file, filename);
		}

		body.append('source', JSON.stringify({ source }));
		body.append('workspaceIds', JSON.stringify(workspaceIds));
		body.append('fileCreate', JSON.stringify({}));
		body.append('isFirstBatch', JSON.stringify({ isFirstBatch }));

		return await this.http
			.post<GetIntegrationExecutionResponse>(this.integrationsUrl('google-docs'), body)
			.catch(this.handleSameExecutionRunningError(ReachIntegration.GOOGLE_DOCS));
	}

	public async executeQuip(
		files: File[],
		workspaceIds: ReachNodeId[],
		source: NodeCreationSource
	): Promise<GetIntegrationExecutionResponse> {
		const body = new FormData();
		for (const file of files) {
			body.append('files', file);
		}

		body.append('source', JSON.stringify({ source }));
		body.append('workspaceIds', JSON.stringify(workspaceIds));
		body.append('fileCreate', JSON.stringify({}));

		return await this.http
			.post<GetIntegrationExecutionResponse>(this.integrationsUrl('quip'), body)
			.catch(this.handleSameExecutionRunningError(ReachIntegration.QUIP));
	}

	public async executeDropboxPaper(
		files: File[],
		workspaceIds: ReachNodeId[],
		source: NodeCreationSource
	): Promise<GetIntegrationExecutionResponse> {
		const body = new FormData();
		for (const file of files) {
			body.append('files', file);
		}

		body.append('source', JSON.stringify({ source }));
		body.append('workspaceIds', JSON.stringify(workspaceIds));
		body.append('fileCreate', JSON.stringify({}));

		return await this.http
			.post<GetIntegrationExecutionResponse>(this.integrationsUrl('dropbox-paper'), body)
			.catch(this.handleSameExecutionRunningError(ReachIntegration.DROPBOX_PAPER));
	}

	public async executeMarkdown(
		files: File[],
		workspaceIds: ReachNodeId[],
		source: NodeCreationSource
	): Promise<GetJobsResponse> {
		const body = new FormData();
		for (const file of files) {
			// Check if the file has a webkitRelativePath property and use it as the filename. We have to do it like this for the Safari browser.
			const filename = file.webkitRelativePath || file.name;
			body.append('files', file, filename);
		}

		body.append('source', JSON.stringify({ source }));
		body.append('workspaceIds', JSON.stringify(workspaceIds));
		body.append('fileCreate', JSON.stringify({}));

		return await this.http
			.post<GetJobsResponse>(this.notesUrl('create-from-markdown'), body)
			.catch(this.handleSameExecutionRunningError(ReachImport.MARKDOWN));
	}

	public async executeMicrosoftWord(
		files: File[],
		workspaceIds: ReachNodeId[],
		source: NodeCreationSource
	): Promise<GetJobsResponse> {
		const body = new FormData();
		for (const file of files) {
			// Check if the file has a webkitRelativePath property and use it as the filename. We have to do it like this for the Safari browser.
			const filename = file.webkitRelativePath || file.name;
			body.append('files', file, filename);
		}

		body.append('source', JSON.stringify({ source }));
		body.append('workspaceIds', JSON.stringify(workspaceIds));
		body.append('fileCreate', JSON.stringify({}));

		return await this.http
			.post<GetJobsResponse>(this.notesUrl('create-from-docx'), body)
			.catch(this.handleSameExecutionRunningError(ReachImport.WORD_DOCX));
	}

	public async executeBookmarks(
		bookmarks: ReachBookmark[],
		browser: BrowserBookmarksBrowser
	): Promise<GetIntegrationExecutionResponse> {
		const body = { bookmarks, browser };
		return await this.http.post<GetIntegrationExecutionResponse>(
			this.integrationsUrl('import-bookmarks'),
			body
		);
	}

	private handleSameExecutionRunningError(integration: ReachIntegrationOrImport) {
		return (err: ClientApiError) => {
			if (
				err.isReachError &&
				err.error.code ===
					ErrorCodes.INTEGRATION_EXECUTION_CANNOT_CREATE_INTEGRATION_EXECUTION_UNTIL_LAST_FINISHES
			) {
				const integrationTypeName: string = this.transloco.translate(
					'integrations.integrations.' + integration + '.name'
				);
				this.hotToastService.error(
					this.transloco.translate('integrations.add_new.errors.same_integration', {
						integrationType: integrationTypeName,
					})
				);
			}

			throw err.error;
		};
	}

	public async getGoogleDriveIntegrationUserData(
		code: string
	): Promise<AuthorizeGoogleDriveIntegrationResponse> {
		return await this.http.post<AuthorizeGoogleDriveIntegrationResponse>(
			this.integrationsUrl('google-drive', 'authorize'),
			{ code }
		);
	}

	public async createGoogleDriveIntegration(
		email: string,
		accessToken: string,
		refreshToken: string,
		driveId: string,
		driveName: string
	): Promise<RegisterGoogleDriveIntegrationResponse> {
		return await this.http.post<RegisterGoogleDriveIntegrationResponse>(
			this.integrationsUrl('google-drive', 'register'),
			{ email, accessToken, refreshToken, driveId, driveName }
		);
	}

	public async getOrRefreshGoogleDriveToken(
		integrationId: string
	): Promise<GetOrRefreshAccessTokenGoogleDriveIntegrationResponse> {
		const body = {
			integrationId,
		};
		return await this.http.post<GetOrRefreshAccessTokenGoogleDriveIntegrationResponse>(
			this.integrationsUrl('google-drive', 'access-token'),
			body
		);
	}

	public async getGoogleDriveSyncedIds(
		integrationId: string
	): Promise<GetSyncedIdsGoogleDriveIntegrationResponse> {
		const params = { integrationId };
		return await this.http.get<GetSyncedIdsGoogleDriveIntegrationResponse>(
			this.integrationsUrl('google-drive', 'synced-ids'),
			params
		);
	}

	public async sendFilesToGoogleDrive(
		integrationId: string,
		rootId: string,
		items: Array<{ fileId: string; folderPath?: Array<{ id: string; name: string }> }>
	): Promise<ExecuteGoogleDriveIntegrationResponse> {
		const body = {
			integrationId,
			files: items,
			rootId,
		};

		return await this.http.post<ExecuteGoogleDriveIntegrationResponse>(
			this.integrationsUrl('google-drive', 'execute'),
			body
		);
	}

	public async setGoogleDriveSyncedState(
		integrationId: string,
		isSync: boolean
	): Promise<UpdateSyncedStateGoogleDriveIntegrationResponse> {
		const body = { integrationId, isSync };
		return await this.http.patch<UpdateSyncedStateGoogleDriveIntegrationResponse>(
			this.integrationsUrl('google-drive', 'synced-state'),
			body
		);
	}

	public async syncGoogleDrive(
		integrationId: string
	): Promise<SyncGoogleDriveIntegrationResponse> {
		const body = { integrationId };
		return await this.http.post<SyncGoogleDriveIntegrationResponse>(
			this.integrationsUrl('google-drive', 'sync'),
			body
		);
	}

	public async syncSitemap(integrationId: string): Promise<SitemapSyncResponse> {
		const body = { integrationId };
		return await this.http.post<SitemapSyncResponse>(
			this.integrationsUrl('sitemap', 'sync'),
			body
		);
	}

	public async setSitemapSyncedState(
		integrationId: string,
		sync: boolean
	): Promise<SetIntegrationSyncStatusResponse> {
		const body = { sync };
		return await this.http.patch<SetIntegrationSyncStatusResponse>(
			this.integrationsUrl(integrationId, 'sync-status'),
			body
		);
	}

	public async setSharepointSyncedState(
		integrationId: string,
		sync: boolean
	): Promise<SetIntegrationSyncStatusResponse> {
		const body = { sync };
		return await this.http.patch<SetIntegrationSyncStatusResponse>(
			this.integrationsUrl(integrationId, 'sync-status'),
			body
		);
	}

	public async getSharepointLoginUrl(
		integrationId?: string
	): Promise<GenerateSharepointAuthUrlResponse> {
		const params: { integrationId?: string } = {};
		if (integrationId) {
			params.integrationId = integrationId;
		}

		return await this.http.get<GenerateSharepointAuthUrlResponse>(
			this.integrationsUrl('sharepoint', 'auth', 'url'),
			params
		);
	}

	public async sendSharepointAuthCode(code: string): Promise<RedeemSharepointAuthCodeResponse> {
		return await this.http.post<RedeemSharepointAuthCodeResponse>(
			this.integrationsUrl('sharepoint', 'auth', 'redeem-code'),
			{
				code,
			}
		);
	}

	public async getSharepointAccessToken(
		integrationId: string
	): Promise<RedeemSharepointAuthCodeResponse> {
		return await this.http.post<RedeemSharepointAuthCodeResponse>(
			this.integrationsUrl('sharepoint', 'auth', 'access-token'),
			{ integrationId }
		);
	}

	public async reauthenticateSharepoint(
		integrationId: string,
		code: string
	): Promise<ReauthenticateSharepointIntegrationResponse> {
		const body: ReauthenticateSharepointIntegrationRequest = { integrationId, code };
		return await this.http.post<ReauthenticateSharepointIntegrationResponse>(
			this.integrationsUrl('sharepoint', 'auth', 'reauthenticate'),
			body
		);
	}

	public async registerSharepointIntegration(
		request: RegisterSharepointIntegrationRequest
	): Promise<RegisterSharepointIntegrationResponse> {
		return await this.http.post<RegisterSharepointIntegrationResponse>(
			this.integrationsUrl('sharepoint', 'register'),
			request
		);
	}

	public async executeSharepoint(
		integrationId: string,
		entries: SharepointEntryInterface[],
		columns?: SharepointColumnSelectionInterface[]
	): Promise<ExecuteSharepointIntegrationResponse> {
		return await this.http.post<ExecuteSharepointIntegrationResponse>(
			this.integrationsUrl('sharepoint', 'execute'),
			{
				integrationId,
				entries,
				columns,
			}
		);
	}

	public async getSharepointColumnSelections(
		request: GetSharepointIntegrationColumnSelectionsRequest
	): Promise<GetSharepointIntegrationColumnSelectionsResponse> {
		return await this.http.get<GetSharepointIntegrationColumnSelectionsResponse>(
			this.integrationsUrl('sharepoint', 'column-selections'),
			{ integrationId: request.integrationId }
		);
	}

	public async updateSharepointColumnSelections(
		request: UpdateSharepointIntegrationColumnSelectionsRequest
	): Promise<UpdateSharepointIntegrationColumnSelectionsResponse> {
		return await this.http.patch<UpdateSharepointIntegrationColumnSelectionsResponse>(
			this.integrationsUrl('sharepoint', 'column-selections'),
			request
		);
	}

	public async syncSharepoint(integrationId: string): Promise<SyncSharepointIntegrationResponse> {
		return await this.http.post<SyncSharepointIntegrationResponse>(
			this.integrationsUrl('sharepoint', 'sync'),
			{ integrationId }
		);
	}

	public async toggleNotifications(
		integrationId: string,
		enabled: boolean
	): Promise<IntegrationSetExecutionNotificationsResponse> {
		const body: IntegrationSetExecutionNotificationsRequest = { enabled };
		return await this.http.patch<IntegrationSetExecutionNotificationsResponse>(
			this.integrationsUrl(integrationId, '/execution-notifications'),
			body
		);
	}

	/**
	 * @deprecated Use `integrationsUrl` or other url method.
	 */
	protected url(...path: Array<string | number>): string {
		return super.url(...path);
	}
}
